/**
 * Wave 29 Chunk 10p — JC site-pin prompt banner.
 *
 * Renders an amber banner at the top of a JobCard when:
 *   • The linked customer has no saved coordinates AND
 *   • The JC has no site_pin yet AND
 *   • The user has not dismissed it this page-load (session-scoped)
 *
 * Two CTAs:
 *   • "Use my current location" — fires requestPosition, calls
 *     `onCaptured({lat, lng, accuracy_m, captured_at})` which the
 *     parent posts to /api/jobcards/{jid}/site-pin.
 *   • "Skip for now" — hides the banner for THIS render; refresh
 *     the page (or open the JC again) to be re-prompted. Per owner
 *     spec: "He must be able to skip, since he might not be at the
 *     site when he opens, but then ask on the next attempt again."
 */
import { useState } from "react";
import { MapPin, AlertTriangle, Loader2 } from "lucide-react";
import { requestPosition } from "../lib/geo";

export default function SitePinPrompt({ job, customer, onCaptured }) {
  const [skipped, setSkipped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Only show when we have a JC + customer loaded.
  if (!job?.id || !customer) return null;
  // Already captured on customer OR on JC → nothing to do.
  if (customer.lat != null && customer.lng != null) return null;
  if (job.site_pin?.lat != null && job.site_pin?.lng != null) return null;
  if (skipped) return null;

  const capture = async () => {
    setBusy(true); setErr(null);
    const loc = await requestPosition({ timeoutMs: 12000 });
    setBusy(false);
    if (loc.denied || loc.lat == null || loc.lng == null) {
      setErr(
        loc.error === "permission_denied"
          ? "Location blocked. Open browser settings → Permissions → Location → Allow, then tap Capture again."
          : loc.error === "geolocation_timeout"
          ? "Took too long to get a fix. Move outside or near a window and try again."
          : "Couldn't capture GPS. Try again."
      );
      return;
    }
    await onCaptured({ lat: loc.lat, lng: loc.lng, accuracy_m: loc.accuracy_m });
  };

  return (
    <div
      className="border border-amber-400/40 bg-amber-400/[0.08] rounded p-3 mb-3 space-y-2"
      data-testid="jc-site-pin-prompt"
    >
      <div className="flex items-start gap-2 text-xs text-amber-200">
        <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="leading-snug">
          <span className="font-semibold">Capture this site's GPS pin —</span>{" "}
          <b>{customer.name}</b> has no saved coordinates yet. Drop a pin while
          you're on site so the team can geofence attendance and route the next
          tech here without an address hunt.
        </div>
      </div>
      {err && (
        <div className="flex items-start gap-2 text-[11px] text-rose-200 border border-rose-500/30 bg-rose-500/10 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={capture}
          disabled={busy}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-60"
          data-testid="jc-site-pin-capture"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
          {busy ? "Getting fix…" : "Use my current location"}
        </button>
        <button
          type="button"
          onClick={() => setSkipped(true)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="jc-site-pin-skip"
        >
          Skip for now
        </button>
        <span className="text-[10px] text-[#647087] self-center">
          We'll ask again next time you open this JC.
        </span>
      </div>
    </div>
  );
}
